/**
 * Prefix diff, word binding, char resolution.
 *
 * The single most bug-prone piece in the project, and free of the DOM, the
 * clock and SpeechRecognition so that every claim about it can be tested rather
 * than demonstrated into a microphone.
 *
 * Three ideas, in the order they matter:
 *
 * **The interim buffer is mutable.** SpeechRecognition revises what it already
 * told you. "I scream" becomes "ice cream" two hundred milliseconds later, and
 * the naive append-only reading produces "I scream ice cream". Every update
 * diffs against what is currently held.
 *
 * **An unchanged prefix keeps its bindings untouched.** Not recomputed to the
 * same value — untouched. Rebinding a word that did not change would resample
 * the mouth buffer at a slightly different moment and give it a new shape, so
 * the front of a sentence would shimmer every time the recogniser revised its
 * tail. That is the acceptance criterion for this phase.
 *
 * **Words are laid out backwards from the sampling point.** Several words
 * usually arrive in a single event, and they were not all spoken at the same
 * instant — they were spoken one after another, ending just before the
 * recogniser reported them. Laying them out forwards from "now" would put the
 * whole phrase in the future.
 */

import { config } from '../core/config.js';
import { gradFor } from '../render/caption.js';

/** Split on whitespace, keeping punctuation attached to its word. */
export function tokenize(text) {
  return String(text ?? '')
    .split(/\s+/)
    .filter(Boolean);
}

/** How many leading entries two arrays share. */
export function commonPrefixLength(a, b) {
  const n = Math.min(a.length, b.length);
  let k = 0;
  while (k < n && a[k] === b[k]) k++;
  return k;
}

/** Spoken duration attributed to a word, from its length. */
function wordDuration(text) {
  const { WORD_DUR_MIN, WORD_DUR_PER_CHAR } = config.speech;
  return Math.max(WORD_DUR_MIN, text.length * WORD_DUR_PER_CHAR);
}

/**
 * When character `i` of `n` was spoken.
 *
 * The centre of the slot, not its leading edge, so a one-character word lands in
 * the middle of its span rather than at the instant before it began.
 *
 * Exported because two things need it and they must agree: this is where a
 * character is *sampled* against the face, and it is also where the emitter
 * *launches* it from the mouth. A letter that left at a different moment from
 * the one it was measured at would be carrying somebody else's face.
 *
 * @param {number} tBind start of the word
 * @param {number} dur spoken duration, ms
 * @param {number} i character index
 * @param {number} n characters in the word
 */
export function charTime(tBind, dur, i, n) {
  return tBind + ((i + 0.5) / n) * dur;
}

/**
 * Resolve one word's characters against the mouth history.
 *
 * Each character is placed at the centre of its slice of the word's span and
 * asks the mouth buffer what the face was doing then. The 3-char moving average
 * afterwards is what makes a line read as a wave rather than as per-letter
 * noise: at ~18-30 Hz, adjacent characters inside a short word often land
 * between the same two samples anyway, and the smoothing keeps the one
 * character that caught a blink or a dropped frame from spiking alone.
 *
 * @param {string} text
 * @param {number} tBind
 * @param {number} dur
 * @param {?{sampleAt: (t: number) => any}} mouthBuffer
 * @returns {import('../core/types.js').CharToken[]}
 */
export function resolveChars(text, tBind, dur, mouthBuffer, audioBuffer = null) {
  const n = text.length;
  const raw = [];

  for (let i = 0; i < n; i++) {
    const t = charTime(tBind, dur, i, n);
    const s = mouthBuffer?.sampleAt(t) ?? null;
    const a = audioBuffer?.sampleAt(t) ?? null;
    raw.push({
      ch: text[i],
      ap: s?.aperture ?? 0,
      wd: s?.width ?? 0.5,
      // Voice quality: how noisy the voice was at the moment this character was
      // spoken. Drives GRAD, which changes apparent weight without changing
      // metrics — so a growled word can be visibly growled without the line
      // reflowing. This is the information ASR discards: a growled "noooo" and
      // a clean one are the same five letters.
      grad: gradFor(a?.flatness),
    });
  }

  const w = config.speech.CHAR_SMOOTH;
  if (w <= 1 || n <= 2) return raw;

  const half = Math.floor(w / 2);
  return raw.map((c, i) => {
    let ap = 0;
    let wd = 0;
    let grad = 0;
    let count = 0;
    for (let j = Math.max(0, i - half); j <= Math.min(n - 1, i + half); j++) {
      ap += raw[j].ap;
      wd += raw[j].wd;
      grad += raw[j].grad;
      count++;
    }
    return { ...c, ap: ap / count, wd: wd / count, grad: grad / count };
  });
}

export class Transcript {
  /**
   * @param {object} [o]
   * @param {?{sampleAt: (t: number) => any}} [o.mouthBuffer]
   */
  constructor({ mouthBuffer = null, audioBuffer = null } = {}) {
    this.mouthBuffer = mouthBuffer;
    /** Feature frames, for the voice quality a word's glyphs carry. */
    this.audioBuffer = audioBuffer;
    /** Frozen. Never mutated again. @type {import('../core/types.js').Word[]} */
    this.committed = [];
    /** Live, and revised on every update. @type {import('../core/types.js').Word[]} */
    this.interim = [];
    /** What the last update did, for the diff log. */
    this.lastDiff = null;
    /**
     * When the most recent interim result arrived, within the current utterance.
     *
     * A final result does not arrive when the words were finished being spoken.
     * It arrives when Chrome decides the utterance is over, which means after a
     * pause long enough to convince it — measured at a median of 2.0 s in one
     * recorded session and 0.6 s in another. That delay is an endpointing
     * decision, not detection latency, and subtracting a 300 ms detection
     * latency from it binds against a moment nearly two seconds after the words
     * were said.
     *
     * Most of the time this is invisible, because the prefix diff keeps the
     * words that did not change and they hold their earlier, correct bindings.
     * It bites exactly when a final revises: one recorded final rebound
     * thirteen words at once, all of them to a mouth position from long after
     * they were spoken. Finals therefore bind from the last interim's clock.
     *
     * @type {?number}
     */
    this._lastInterimAt = null;
  }

  /** Committed words followed by the live tail. */
  get words() {
    return [...this.committed, ...this.interim];
  }

  get text() {
    return this.words.map((w) => w.text).join(' ');
  }

  reset() {
    this.committed = [];
    this.interim = [];
    this.lastDiff = null;
    this._lastInterimAt = null;
  }

  /**
   * Fold one recogniser result into the transcript.
   *
   * @param {object} o
   * @param {string} o.text        the full interim (or final) hypothesis
   * @param {boolean} [o.isFinal]
   * @param {number} o.now         performance.now() when the result arrived
   * @param {number} [o.samplingPoint]
   *   Spoken time that the last word ends at. Defaults to `now` minus the ASR
   *   detection latency — the point of the whole exercise is to bind a word to
   *   the face as it was when the word was *said*, not when it was reported.
   *   (SPEC1.md says "now - LAG" here; LAG is the display delay and belongs to
   *   the renderer. Using it would bind against the wrong moment whenever the
   *   two are set differently, which is most of the time.)
   * @returns {{kept: number, added: number, dropped: number, finalised: number}}
   */
  update({ text, isFinal = false, now, samplingPoint }) {
    // A final's own arrival time is not when the speech happened — see
    // `_lastInterimAt`. Bind from the last interim of this utterance instead,
    // falling back to `now` when a final arrives with no interim before it.
    const reportedAt = isFinal ? (this._lastInterimAt ?? now) : now;
    const end = samplingPoint ?? reportedAt - config.latency.asr;
    const incoming = tokenize(text);
    const current = this.interim.map((w) => w.text);

    const kept = commonPrefixLength(current, incoming);
    const dropped = Math.max(0, current.length - incoming.length);

    // [0, kept) survive by reference. Not recomputed — untouched.
    const head = this.interim.slice(0, kept);

    // [kept, ..) are new, and are laid out backwards so the last of them ends
    // at the sampling point.
    const tail = [];
    let cursor = end;
    for (let i = incoming.length - 1; i >= kept; i--) {
      const wordText = incoming[i];
      const dur = wordDuration(wordText);
      const tBind = cursor - dur;
      tail.unshift({
        text: wordText,
        tBind,
        dur,
        chars: resolveChars(wordText, tBind, dur, this.mouthBuffer, this.audioBuffer),
        isFinal: false,
      });
      cursor = tBind;
    }

    this.interim = [...head, ...tail];

    let finalised = 0;
    if (isFinal) {
      // Frozen for good. Rule 4 of the architecture section: once a word is
      // committed its spans are never mutated again, which is also what makes
      // the renderer's "freeze finalised words" optimisation safe.
      finalised = this.interim.length;
      for (const w of this.interim) w.isFinal = true;
      this.committed.push(...this.interim);
      this.interim = [];
      // The next utterance gets its own clock.
      this._lastInterimAt = null;
    } else {
      this._lastInterimAt = now;
    }

    this.lastDiff = { kept, added: tail.length, dropped, finalised, at: now, boundAt: reportedAt };
    return this.lastDiff;
  }
}

export default Transcript;
